import { AuthProvider, useAuth } from 'react-oidc-context';
import { useEffect, useState, type ReactNode } from 'react';
import { WebStorageStateStore } from 'oidc-client-ts';
import { config } from './config';
import { api, setTokenGetter } from './api';

const oidcConfig = {
  authority: config.cognito.authority,
  client_id: config.cognito.clientId,
  redirect_uri: config.cognito.redirectUri,
  post_logout_redirect_uri: config.cognito.postLogoutRedirectUri,
  scope: config.cognito.scope,
  response_type: 'code',
  automaticSilentRenew: true,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

export function AppAuthProvider({ children }: { children: ReactNode }): JSX.Element {
  if (!config.cognito.authority || !config.cognito.clientId) {
    // ローカル開発時は OIDC を通さず素通し
    return <>{children}</>;
  }
  return <AuthProvider {...oidcConfig}>{children}</AuthProvider>;
}

export function useCurrentUser(): {
  userId: string;
  name: string;
  email?: string;
  initial: string;
  isAdmin: boolean;
} {
  const auth = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);

  const profile = auth?.user?.profile;
  const userId = (profile?.sub as string) ?? '';
  const email = profile?.email as string | undefined;
  const familyGiven = [profile?.family_name, profile?.given_name].filter(Boolean).join(' ');
  const name =
    (profile?.name as string) ||
    (profile?.preferred_username as string) ||
    familyGiven ||
    email ||
    'ユーザー';

  useEffect(() => {
    let cancelled = false;
    api.me().then((m) => {
      if (!cancelled) setIsAdmin(!!m.isAdmin);
    }).catch(() => {
      if (!cancelled) setIsAdmin(false);
    });
    return () => {
      cancelled = true;
    };
  }, [auth?.user?.access_token]);

  return { userId, name, email, initial: name.slice(0, 1), isAdmin };
}

export function useSetupAuthToken(): void {
  const auth = useAuth();
  // API Gateway の CognitoUserPoolsAuthorizer は id_token を検証する (aud claim を含むため)。
  // access_token だと aud が無く 401 になるので、id_token を Bearer に載せる。
  setTokenGetter(() => auth?.user?.id_token);
}
